import React, { useState, useEffect } from 'react';

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    budget: '',
    timeline: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isVisible, setIsVisible] = useState({
    form: false,
    info: false,
    faq: false
  });

  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setIsVisible(prev => ({
            ...prev,
            [sectionId]: true
          }));
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    const sections = ['contact-form', 'contact-info', 'contact-faq'];
    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });
    
    return () => {
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formState.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Create FormData object
        const formData = new FormData();
        
        // Add form fields to FormData
        Object.entries(formState).forEach(([key, value]) => {
          formData.append(key, value);
        });
        
        // Send data to PHP script
        const response = await fetch('/contact-form.php', {
          method: 'POST',
          body: formData,
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Show success message
          setIsSubmitted(true);
          
          // Reset form
          setFormState({
            name: '',
            email: '',
            phone: '',
            company: '',
            serviceInterest: '',
            budget: '',
            timeline: '',
            message: ''
          });
          
          // Reset success message after 5 seconds
          setTimeout(() => {
            setIsSubmitted(false);
          }, 5000);
        } else {
          // Show error message
          setSubmitError(result.message || 'Failed to send message. Please try again later.');
        }
      } catch (error) {
        setSubmitError('An error occurred. Please try again later.');
        console.error('Form submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const services = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'app-development', label: 'Mobile App Development' },
    { value: 'ui-ux-design', label: 'UI/UX Design' },
    { value: 'ecommerce', label: 'E-Commerce Solutions' },
    { value: 'custom-software', label: 'Custom Software' },
    { value: 'it-consultancy', label: 'IT Consultancy' }
  ];
  
  const budgetRanges = [
    { value: '', label: 'Select a budget range' },
    { value: 'under-5k', label: 'Under €5,000' },
    { value: '5k-10k', label: '€5,000 - €10,000' },
    { value: '10k-25k', label: '€10,000 - €25,000' },
    { value: '25k-50k', label: '€25,000 - €50,000' },
    { value: 'over-50k', label: 'Over €50,000' },
    { value: 'not-sure', label: 'Not sure yet' }
  ];
  
  const timelineOptions = [
    { value: '', label: 'Select a timeline' },
    { value: 'asap', label: 'As soon as possible' },
    { value: '1-3-months', label: '1-3 months' },
    { value: '3-6-months', label: '3-6 months' },
    { value: '6-plus-months', label: '6+ months' },
    { value: 'not-sure', label: 'Not sure yet' }
  ];

  const faqs = [
    {
      question: 'What is the typical project timeline?',
      answer: 'Project timelines vary based on scope and complexity. Simple websites might take 4-6 weeks, while complex applications can take 3-6 months. Well provide a detailed timeline during consultation.'
    },
    {
      question: 'How much does a project cost?',
      answer: 'Each project is unique, and pricing depends on requirements, complexity, and timeline. We offer both fixed-price and time-and-materials pricing models based on your needs. Contact us for a free quote.'
    },
    {
      question: 'Do you offer support after launch?',
      answer: 'Yes, we provide ongoing maintenance and support services. We offer different support packages based on your needs, from basic technical support to comprehensive maintenance.'
    },
    {
      question: 'How do we get started?',
      answer: 'The process begins with a consultation where we discuss your goals, requirements, and timeline. From there, well develop a proposal and, once approved, begin the design and development process.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1 className="hero-title animate-fadeUp">Let's Create Something <span>Amazing</span> Together</h1>
          <p className="hero-description animate-fadeUp delay-1">
            Have a project in mind? Let's discuss how we can help you achieve your goals and bring your vision to life.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            {/* Contact Information */}
            <div id="contact-info" className={`${isVisible.info ? 'animate-fadeUp' : ''}`}>
              <h2 className="mb-4 text-gradient">Get In Touch</h2>
              <p className="mb-5">
                Whether you have a question about our services, want to start a project, or just want to say hello, 
                we're here to help. Fill out the form, and we'll get back to you as soon as possible.
              </p>
              
              <div className="mb-5">

                
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <i className="fas fa-phone-alt"></i>
                  </div>
                  <div className="contact-info-content">
                    <h4>Call Us</h4>
                    <p>+353 1 234 5678</p>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="contact-info-content">
                    <h4>Email Us</h4>
                    <p>info@opitech.ie</p>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="contact-info-content">
                    <h4>Working Hours</h4>
                    <p>Monday to Friday: 9AM - 6PM</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="mb-3">Follow Us</h3>
                <div className="footer-social">
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                    <i className="fab fa-instagram"></i>
                  </a>
                  <a href="https://dribbble.com" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                    <i className="fab fa-dribbble"></i>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div id="contact-form" className={`${isVisible.form ? 'animate-fadeUp' : ''}`}>
              <div className="contact-form">
                <h2 className="mb-4">Send Us a Message</h2>
                
                {isSubmitted ? (
                  <div style={{ 
                    padding: '2.5rem', 
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(16, 185, 129, 0.1))', 
                    borderRadius: 'var(--radius-xl)',
                    textAlign: 'center',
                    border: '1px solid rgba(129, 140, 248, 0.3)'
                  }}>
                    <div style={{ fontSize: '3.5rem', color: 'var(--secondary)', marginBottom: '1.25rem' }}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <h3 className="mb-2">Thank You!</h3>
                    <p>Your message has been sent successfully. We'll get back to you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        Full Name <span style={{ color: 'var(--primary-light)' }}>*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formState.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your name"
                      />
                      {errors.name && <p style={{ color: 'var(--danger)', marginTop: '0.5rem', fontSize: '0.875rem' }}>{errors.name}</p>}
                    </div>
                    
                    <div className="grid grid-2" style={{ gap: '1.25rem' }}>
                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email <span style={{ color: 'var(--primary-light)' }}>*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formState.email}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Your email"
                        />
                        {errors.email && <p style={{ color: 'var(--danger)', marginTop: '0.5rem', fontSize: '0.875rem' }}>{errors.email}</p>}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="phone" className="form-label">
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formState.phone}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Your phone number"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="company" className="form-label">
                        Company
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={formState.company}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your company"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="serviceInterest" className="form-label">
                        Service Interested In
                      </label>
                      <select
                        id="serviceInterest"
                        name="serviceInterest"
                        value={formState.serviceInterest}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select a service</option>
                        {services.map(service => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-2" style={{ gap: '1.25rem' }}>
                      <div className="form-group">
                        <label htmlFor="budget" className="form-label">
                          Budget Range
                        </label>
                        <select
                          id="budget"
                          name="budget"
                          value={formState.budget}
                          onChange={handleChange}
                          className="form-input"
                        >
                          {budgetRanges.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="timeline" className="form-label">
                          Project Timeline
                        </label>
                        <select
                          id="timeline"
                          name="timeline"
                          value={formState.timeline}
                          onChange={handleChange}
                          className="form-input"
                        >
                          {timelineOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="message" className="form-label">
                        Message <span style={{ color: 'var(--primary-light)' }}>*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        className="form-input form-textarea"
                        placeholder="Tell us about your project"
                        rows="6"
                      ></textarea>
                      {errors.message && <p style={{ color: 'var(--danger)', marginTop: '0.5rem', fontSize: '0.875rem' }}>{errors.message}</p>}
                    </div>
                    
                    {submitError && (
                      <div style={{ 
                        padding: '1rem', 
                        marginBottom: '1.5rem',
                        background: 'rgba(239, 68, 68, 0.1)', 
                        color: 'var(--danger)',
                        borderRadius: 'var(--radius)',
                        borderLeft: '3px solid var(--danger)'
                      }}>
                        <p style={{ marginBottom: 0, fontSize: '0.875rem' }}><i className="fas fa-exclamation-circle mr-2"></i> {submitError}</p>
                      </div>
                    )}
                    
                    <button 
                      type="submit" 
                      className="btn btn-icon" 
                      style={{ width: '100%' }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span>Sending...</span>
                          <i className="fas fa-circle-notch fa-spin ml-1"></i>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <i className="fas fa-paper-plane ml-1"></i>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Map Section */}
      <section className="section" style={{ backgroundColor: 'rgba(17, 24, 39, 0.4)' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2>Find Us</h2>
            <p>Our office is centrally located in Dublin's creative district, making it easy to visit us.</p>
          </div>
          
          <div style={{ 
            height: '450px', 
            backgroundColor: 'rgba(31, 41, 55, 0.5)', 
            borderRadius: 'var(--radius-xl)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--gray)',
            overflow: 'hidden',
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            {/* In a real project, this would be a Google Map or similar */}
            <div style={{ 
              position: 'absolute', 
              width: '100%', 
              height: '100%', 
              background: 'url(/images/map-bg.jpg) center/cover no-repeat',
              opacity: 0.2
            }}></div>
            <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '1.25rem', color: 'var(--primary-light)' }}>
                <i className="fas fa-map-marked-alt"></i>
              </div>
              <p style={{ fontSize: '1.25rem', maxWidth: '500px', margin: '0 auto' }}>
                Interactive map would be displayed here showing our location in central Dublin
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section id="contact-faq" className={`section ${isVisible.faq ? 'animate-fadeUp' : ''}`}>
        <div className="container">
          <div className="text-center mb-5">
            <h2>Frequently Asked Questions</h2>
            <div className="divider"></div>
          </div>
          
          <div className="grid grid-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="card" 
                style={{ 
                  padding: '2rem',
                  height: '100%',
                  animationDelay: `${0.1 * index}s`
                }}
              >
                <h3 className="mb-3" style={{ color: 'var(--primary-light)' }}>{faq.question}</h3>
                <p style={{ marginBottom: 0 }}>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="cta-section">
        <div className="container cta-content">
          <h2 className="cta-title">Ready to Start Your Project?</h2>
          <p className="cta-description">
            Contact us today for a free consultation and let's discuss how we can help you achieve your business goals.
          </p>
          <button 
            className="btn btn-secondary btn-icon"
            onClick={() => {
              const formElement = document.querySelector('.contact-form');
              if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <span>Get a Free Consultation</span>
            <i className="fas fa-arrow-right ml-1"></i>
          </button>
        </div>
      </section>
    </>
  );
};

export default Contact;